import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button, Tooltip, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import RemoveIcon from '@material-ui/icons/Remove';
import format from 'date-fns/format';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory from 'react-bootstrap-table2-filter';
import Loading from '../../../components/Loading';
import fontSize from '../../../styles/settings/fontSize';
import { convertForRealFormatted } from '../../../utils/convertForRealFormatted';
import { padLeadingZeros } from '../../../utils/padLeadingZeros';
import * as S from './styles';

function NotesTable({
  provider,
  updatedNotes,
  screenControls,
  setNotesToRemove,
  setNotesToAdd,
  isLoading,
  statusPropose,
  maxHeight
}) {
  const [rowsSelected, setRowsSelected] = useState([]);

  const handleOnSelectAll = useCallback((isSelect, rows) => {
    if (isSelect) {
      setRowsSelected(rows);
    } else {
      setRowsSelected([]);
    }
  }, []);

  const handleOnSelect = useCallback((row, isSelect, rowIndex, e) => {
    if (e) {
      e.stopPropagation();
    }
    setRowsSelected(prevSelected => {
      if (isSelect) {
        // Verifica se a linha já não está selecionada para evitar duplicatas
        const isAlreadySelected = prevSelected.some((x) => x.tituloId === row.tituloId);
        if (!isAlreadySelected) {
          return [...prevSelected, row];
        }
        return prevSelected;
      } else {
        return prevSelected.filter((x) => x.tituloId !== row.tituloId);
      }
    });
  }, []);

  const selectedIds = useMemo(() => {
    return rowsSelected.map(row => row.tituloId);
  }, [rowsSelected]);

  const nonSelectableIds = useMemo(() => {
    return updatedNotes?.filter(note => note.status?.toLowerCase() === 'removido').map(note => note.tituloId) || [];
  }, [updatedNotes]);

  const selectRow = useMemo(() => ({
    mode: 'checkbox',
    clickToSelect: true,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
    hideSelectColumn: !screenControls?.botoesAdicionarRemoverTitulos,
    selected: selectedIds,
    nonSelectable: nonSelectableIds,
  }), [selectedIds, nonSelectableIds, screenControls?.botoesAdicionarRemoverTitulos, handleOnSelect, handleOnSelectAll]);

  const reprintColorTable = () => {
    setTimeout(() => {
      const elements = document.getElementsByClassName('has-action-btn');
      if (elements) {
        for (const item of elements) {
          item.closest('tr').style.backgroundColor = '';
        }
      }
    }, 50);
    setTimeout(() => {
      const elements = document.getElementsByClassName('remove-btn-notes');
      if (elements) {
        for (const item of elements) {
          item.closest('tr').style.backgroundColor = '';
          item.closest('tr').style.backgroundColor = '#e57373';
        }
      }
    }, 150);
  };

  const printColorTable = () => {
    setTimeout(() => {
      const elements = document.getElementsByClassName('removed-item');
      if (elements) {
        for (const item of elements) {
          item.closest('tr').style.backgroundColor = '';
          item.closest('tr').style.backgroundColor = '#e57373';
        }
      }
    }, 150);
  };

  const renderIcon = (note) => {
    if (note.status === 'Disponivel') {
      return (
        <Tooltip placement="top" title="Remover Nota">
          <Button
            className="action-btn"
            onClick={() => {
              setNotesToRemove(note);
              setNotesToAdd(0);
            }}
          >
            <RemoveIcon style={{ fontSize: fontSize.tableRowText }} />
          </Button>
        </Tooltip>
      );
    }
    if (note.status === 'Removido') {
      return (
        <Tooltip placement="top" title="Adicionar Nota">
          <Button
            className="action-btn remove-btn-notes"
            onClick={() => {
              setNotesToAdd(note);
              setNotesToRemove(0);
            }}
          >
            <AddIcon style={{ fontSize: fontSize.tableRowText }} />
          </Button>
        </Tooltip>
      );
    }
    return <></>;
  };

  const addNotes = () => {
    setNotesToAdd(rowsSelected);
    setRowsSelected([]);
  };
  const removeNotes = () => {
    setNotesToRemove(rowsSelected);
    setRowsSelected([]);
  };

  useEffect(() => {
    // Garantir que os checkboxes sejam clicáveis após a renderização
    if (!provider || statusPropose || isLoading) {
      return;
    }
    
    const enableCheckboxes = () => {
      const checkboxes = document.querySelectorAll('.selection-cell input[type="checkbox"], .selection-cell-header input[type="checkbox"]');
      const selectionCells = document.querySelectorAll('.selection-cell, .selection-cell-header');
      
      checkboxes.forEach((checkbox) => {
        checkbox.style.pointerEvents = 'auto';
        checkbox.style.cursor = 'pointer';
        checkbox.style.zIndex = '999';
        checkbox.style.position = 'relative';
        checkbox.setAttribute('tabindex', '0');
      });
      
      selectionCells.forEach((cell) => {
        cell.style.pointerEvents = 'auto';
        cell.style.zIndex = '999';
        cell.style.position = 'relative';
      });
    };
    
    // Executar após a renderização
    const timeoutId = setTimeout(enableCheckboxes, 200);
    
    // Observar mudanças na tabela
    const observer = new MutationObserver(() => {
      setTimeout(enableCheckboxes, 100);
    });
    const tableContainer = document.querySelector('.react-bootstrap-table');
    if (tableContainer) {
      observer.observe(tableContainer, { childList: true, subtree: true });
    }
    
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [updatedNotes, provider, statusPropose, isLoading]);

  if (!provider || statusPropose) {
    return (
      <S.TableTitleHeader>
        <h3 className="operation-title">Notas</h3>
      </S.TableTitleHeader>
    );
  }

  if (isLoading) {
    return (
      <>
        <S.TableTitleHeader>
          <h3 className="operation-title">Notas</h3>
        </S.TableTitleHeader>
        <Loading />
      </>
    );
  }

  if (provider && !isLoading) {
    printColorTable();
  }

  const columns = [
    // {
    //   text: 'Ação',
    //   headerFormatter: () => <PlaylistAddCheckIcon className="check-icon" />,
    //   headerAlign: 'center',
    //   dataField: 'actions',
    //   align: 'center',
    //   formatter: (cell, rowData) => {
    //     return screenControls?.botoesAdicionarRemoverTitulos ? renderIcon(rowData) : <></>;
    //   },
    // },
    {
      text: 'Seq.',
      dataField: 'sequencia',
      className: 'text-right',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell, rowData) => {
        return (
          <span
            className={
              rowData.status?.toLowerCase() === 'removido'
                ? 'removed-item text-right-block'
                : 'text-right-block'
            }
          >
            {cell}
          </span>
        );
      },
    },
    {
      text: 'Prz.',
      dataField: 'prazo',
      className: 'text-right',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => {
        return <span className="text-right-block">{cell}</span>;
      },
    },
    {
      text: 'Loja',
      dataField: 'loja',
      sort: true,
      headerClasses: 'width-column-loja',
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => {
        return (
          <Tooltip placement="top" title={cell || ''}>
            <span className="text-left-block">
              {cell && cell.length >= 30 ? `${cell.slice(0, 30)}...` : cell || ''}
            </span>
          </Tooltip>
        );
      },
    },
    {
      text: 'Nº Nota',
      dataField: 'numeroNota',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => {
        return <span className="text-left-block">{cell}</span>;
      },
    },
    {
      text: 'Emissão',
      dataField: 'dataEmissao',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      sortFunc: (a, b, order, dataField, rowA, rowB) => {
        if (order === 'asc') {
          return Date.parse(a) - Date.parse(b);
        } else if (order === 'desc') {
          return Date.parse(b) - Date.parse(a);
        }
      },
      formatter: (cell) => {
        return <span className="text-center-block">{format(new Date(cell), 'dd/MM/yy')}</span>;
      },
    },
    {
      text: 'Vencto',
      dataField: 'dataVencimento',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      sortFunc: (a, b, order, dataField, rowA, rowB) => {
        if (order === 'asc') {
          return Date.parse(a) - Date.parse(b);
        } else if (order === 'desc') {
          return Date.parse(b) - Date.parse(a);
        }
      },
      formatter: (cell) => {
        return <span className="text-center-block">{format(new Date(cell), 'dd/MM/yy')}</span>;
      },
    },
    {
      text: 'Valor Bruto',
      dataField: 'valorBruto',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => (
        <span className="text-right-block">{convertForRealFormatted(cell, 2)}</span>
      ),
    },
    {
      text: 'Desconto',
      dataField: 'valorDesconto',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => (
        <span className="text-right-block">{convertForRealFormatted(cell, 2)}</span>
      ),
    },
    {
      text: 'Valor a Pagar',
      dataField: 'valorLiquido',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => (
        <span className="text-right-block">{convertForRealFormatted(cell, 2)}</span>
      ),
    },
    {
      text: 'Obs.',
      dataField: 'observacao',
      sort: true,
      onSort: (field, order) => reprintColorTable(),
      formatter: (cell) => (
        <Tooltip placement="top" title={cell || ''}>
          <span>{cell && cell.length >= 30 ? `${cell.slice(0, 30)}...` : cell || ''}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <S.TableContent className="without-scroll">
      <S.TableTitleHeader>
        <Grid container>
          {screenControls?.botoesAdicionarRemoverTitulos ? (
            <Grid item xs={1}>
              <Tooltip placement="top" title={'Adicionar'}>
                <span
                  type="button"
                  style={{
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                  }}
                  onClick={addNotes}
                >
                  +
                </span>
              </Tooltip>
              <Tooltip placement="top" title={'Remover'}>
                <span
                  style={{
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    paddingLeft: '5px',
                  }}
                  onClick={removeNotes}
                >
                  -
                </span>
              </Tooltip>
            </Grid>
          ) : (
            ''
          )}
          <Grid
            item
            xs={11}
            justifyContent="center"
            alignContent="center"
            style={{ textAlign: 'center', paddingRight: '140px' }}
          >
            <h3 className="operation-title">Notas</h3>
          </Grid>
        </Grid>
      </S.TableTitleHeader>
      <S.HeightTableContainer maxHeight={maxHeight}>
        <BootstrapTable
          columns={columns}
          data={updatedNotes}
          keyField="tituloId"
          striped
          hover
          condensed
          selectRow={selectRow}
        />
      </S.HeightTableContainer>
    </S.TableContent>
  );
}

export default NotesTable;
